import type {
  ConfidenceSignalGroup,
  ExpertBonusSignal,
  ExpertConfidenceSignal,
} from "@/app/services/confidenceSignalsApi.service";

export interface ExpertSignalSummary {
  /** Points from definitions the expert holds with an enabled assignment. */
  earnedPoints: number;
  /** The ceiling: every globally enabled definition in scope for this expert. */
  availablePoints: number;
  assignedCount: number;
  earningCount: number;
  /**
   * Bonus contribution after the API's cap rule. Never recomputed here from
   * `points`: whether a bonus fits depends on group headroom and `sort_index`
   * ordering, so `applied_points` is the only trustworthy figure.
   */
  bonusPoints: number;
  /** What the public profile reports: definitions plus applied bonuses. */
  totalPoints: number;
}

export interface ExpertSignalGroupView extends ExpertSignalSummary {
  key: string;
  label: string;
  sortIndex: number;
  signals: ExpertConfidenceSignal[];
  bonusSignals: ExpertBonusSignal[];
}

export type SortDirection = "up" | "down";

export function parseNullableNFactor(value: string | undefined): number | null {
  return value?.trim() ? Number(value) : null;
}

export function isValidNFactor(value: number | null): boolean {
  return value === null || (Number.isFinite(value) && value >= 0);
}

export function getAdjacentSortSwap<T extends { key: string; sort_index: number }>(
  items: T[],
  itemKey: string,
  direction: SortDirection,
): [T, T] | null {
  const ordered = [...items].sort(
    (a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key),
  );
  const currentIndex = ordered.findIndex((item) => item.key === itemKey);
  const targetIndex = currentIndex + (direction === "up" ? -1 : 1);

  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return null;
  }

  return [ordered[currentIndex]!, ordered[targetIndex]!];
}

export function buildExpertSignalSummary(
  signals: ExpertConfidenceSignal[],
  bonusSignals: ExpertBonusSignal[] = [],
): ExpertSignalSummary {
  const summary = signals.reduce<ExpertSignalSummary>(
    (totals, signal) => {
      const globallyEnabled = signal.status === "enabled";
      const earnsPoints =
        globallyEnabled && signal.has_signal && signal.expert_status === "enabled";

      if (globallyEnabled) totals.availablePoints += signal.points;
      if (signal.has_signal) totals.assignedCount += 1;
      if (earnsPoints) {
        totals.earnedPoints += signal.points;
        totals.earningCount += 1;
      }

      return totals;
    },
    {
      earnedPoints: 0,
      availablePoints: 0,
      assignedCount: 0,
      earningCount: 0,
      bonusPoints: 0,
      totalPoints: 0,
    },
  );

  summary.bonusPoints = bonusSignals.reduce(
    (total, bonusSignal) => total + (bonusSignal.applied ? bonusSignal.applied_points : 0),
    0,
  );
  summary.totalPoints = summary.earnedPoints + summary.bonusPoints;

  return summary;
}

/**
 * Bonus signals sort after the group's definitions, matching the public
 * payload and the order the cap rule considers them in.
 */
export function sortExpertBonusSignals(bonusSignals: ExpertBonusSignal[]): ExpertBonusSignal[] {
  return [...bonusSignals].sort((a, b) => a.sort_index - b.sort_index || a.id - b.id);
}

export function groupExpertSignals(
  signals: ExpertConfidenceSignal[],
  groups: Pick<ConfidenceSignalGroup, "key" | "label" | "sort_index">[],
  bonusSignals: ExpertBonusSignal[] = [],
): ExpertSignalGroupView[] {
  const groupMap = new Map(groups.map((group) => [group.key, group]));
  const keys = new Set([
    ...groups.map((group) => group.key),
    ...signals.map((signal) => signal.group_key),
    ...bonusSignals.map((bonusSignal) => bonusSignal.group_key),
  ]);

  return [...keys]
    .map((key) => {
      const definition = groupMap.get(key);
      const groupSignals = signals
        .filter((signal) => signal.group_key === key)
        .sort((a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key));
      const groupBonusSignals = sortExpertBonusSignals(
        bonusSignals.filter((bonusSignal) => bonusSignal.group_key === key),
      );

      return {
        key,
        label: definition?.label ?? key.replace(/_/g, " "),
        sortIndex: definition?.sort_index ?? Number.MAX_SAFE_INTEGER,
        signals: groupSignals,
        bonusSignals: groupBonusSignals,
        ...buildExpertSignalSummary(groupSignals, groupBonusSignals),
      };
    })
    .filter((group) => group.signals.length > 0 || group.bonusSignals.length > 0)
    .sort((a, b) => a.sortIndex - b.sortIndex || a.key.localeCompare(b.key));
}
